/*
 * Ryan O'Dowd
 * 2024-09-27
 * © Copyright 2024 Oakwood Software Consulting, Inc.  All Rights Reserved.
 */
import React, {
  useState,
} from 'react';
import Button from '@components/common/Button';
import EditSchoolDialog from '@website/components/EditSchoolDialog';
import IconButton from '@components/common/IconButton';
import NewOrEditClassroomDialog from './NewOrEditClassroomDialog';
import Table from '@components/common/Table';
import Text from '@components/common/Text';
import {
  View,
} from 'react-native';
import getStyles from './styles';
import hooks from '@hooks';
import {
  masqueradeAs,
} from '@utils/store/actions';

const SchoolPreview = (props: SchoolPreviewProps) => {
  const styles = hooks.useStyles(getStyles);
  const dispatch = hooks.useTypedDispatch();
  const navigation = hooks.useNavigation();

  const [_isEditSchoolDialogOpen, setOrEditSchoolDialogOpen] = useState(false);
  const [_isNewOrEditClassroomDialogOpen, setIsNewOrEditClassroomDialogOpen] = useState(false);
  const [_selectedClassroom, setSelectedClassroom] = useState(null);

  const tableRows = props.school.classrooms.map((classroom) => {
    return [
      {
        id: `${classroom.id}_name`,
        content: (
          <Text
            style={styles.nameAndLink}
            isLink={true}
            onPress={() => navigation.navigate(`/schools/${props.school.id}/classrooms/${classroom.id}`)}
          >
            {classroom.name}
          </Text>
        ),
        sortValue: classroom.name.toLocaleLowerCase(),
      },
      {
        id: `${classroom.id}_teacher`,
        content: (
          <View style={styles.teacherNameAndActions}>
            <Text>{classroom.teacher.display_name}</Text>
            {/* @TODO:
            <IconButton
              mode='outlined'
              icon='pencil'
              iconColor={styles.colors.primary}
              onPress={() => {
                // @TODO: implement: edit dialog for teacher
              }}
            />
            */}
            {props.isWrinklyAdmin &&
              <IconButton// @TODO: this option should not be available for school personnel, only wrinkly admins
                icon='drama-masks'
                iconColor={styles.colors.primary}
                onPress={() => {
                  dispatch(masqueradeAs(classroom.teacher.user_id, () => navigation.navigate(`/schools/${props.school.id}/classrooms/${classroom.id}`)));
                }}
              />
            }
          </View>
        ),
        sortValue: classroom.teacher.display_name?.toLocaleLowerCase(),
      },
      {
        id: `${classroom.id}_students`,
        content: <Text>{classroom.student_count}</Text>,
        sortValue: classroom.student_count,
      },
      {
        id: `${classroom.id}_join_code`,
        content: <Text style={styles.joinCode}>{classroom.is_join_code_enabled ? classroom.join_code : '-'}</Text>,
        sortValue: (classroom.join_code ?? '').toLocaleLowerCase(), // @TODO: really doesn't need to be sortable
      },
      {
        id: `${classroom.id}_link`,
        content: (
          <IconButton
            icon='pencil'
            iconColor={styles.colors.primary}
            onPress={() => {
              setSelectedClassroom(classroom);
              setIsNewOrEditClassroomDialogOpen(true);
            }}
          />
        ),
        sortValue: 0, // @TODO: really doesn't need to be sortable
      },
    ].filter(Boolean);
  });

  return (
    <React.Fragment>
      <Table
        leftItem={props.isWrinklyAdmin &&
          <View>
            <IconButton
              style={styles.editButton}
              icon='pencil'
              size={24}
              iconColor={styles.colors.primaryExtraLight}
              onPress={() => {
                setOrEditSchoolDialogOpen(true);
              }}
            />
          </View>
        }
        tableTitle={props.title}
        open={props.defaultOpen}
        headerRowData={[
          {width: 230, id: 'name', content: 'Classroom name'},
          {width: 150, id: 'teacher', content: 'Teacher'},
          {width: 140, id: 'students', content: 'Number of active students', rightAlign: true},
          {width: 100, id: 'join_code', content: 'Join code', rightAlign: true},
          {width: 50, id: 'edit', content: 'Edit', rightAlign: true},
        ].filter(Boolean)}
        isSortable={true}
        orderByIndex={2}// @TODO: add active dates and order by those instead
        rowData={tableRows}// @TODO: shouldn't be clickable (or if they are, make individually updatable)
        filtersJsx={
          <Button
            theme={styles.colors.accent}
            onPress={() => setIsNewOrEditClassroomDialogOpen(true)}
          >
            Add classroom
          </Button>
        }
      />

      {_isEditSchoolDialogOpen && <EditSchoolDialog school={props.school} onClose={() => setOrEditSchoolDialogOpen(false)} />}
      {_isNewOrEditClassroomDialogOpen &&
        <NewOrEditClassroomDialog
          schoolId={props.school.id}
          classroom={_selectedClassroom}
          onClose={() => {
            setIsNewOrEditClassroomDialogOpen(false);
            setSelectedClassroom(null); // @TODO: hacky that i have to do this every time the dialog is closed...either listen for that in useeffect or get rid of this state
          }}
        />
      }
    </React.Fragment>
  );
};

class SchoolPreviewProps {
  school: any;
  title: string;

  defaultOpen?: boolean;
  isWrinklyAdmin?: boolean;
};

export default SchoolPreview;
